import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useContent } from '../content/ContentProvider';
import '../styles/glass.css';

export const PortfolioPage: React.FC = () => {
  const navigate = useNavigate();
  const { portfolioPage } = useContent();
  const portfolioItems = portfolioPage.items;

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-lavender-50 via-cream-50 to-rose-50 relative overflow-hidden">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 right-[10%] w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(225, 36, 124, 0.4) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 60, 0],
            y: [0, -40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 left-[10%] w-[450px] h-[450px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(202, 95, 223, 0.4) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, -50, 0],
            y: [0, 50, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container relative z-10 mx-auto max-w-7xl px-6 md:px-12 lg:px-20 py-32">
        {/* Back Button */}
        <motion.button
          onClick={() => navigate('/')}
          className="glass-card mb-12 px-6 py-3 rounded-full font-semibold text-dark-600 overflow-hidden group hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="glass-card-highlight" />
          <div className="glass-card-shine group-hover:opacity-100" />
          <div className="glass-card-glow" />
          <svg className="relative z-10 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="relative z-10">{portfolioPage.back}</span>
        </motion.button>

        {/* Title Section */}
        <motion.div
          className="mb-20 text-center"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-dark-600 mb-6">
            {portfolioPage.title}
          </h1>
          <p className="text-xl md:text-2xl text-dark-400 max-w-3xl mx-auto leading-relaxed">
            {portfolioPage.subtitle}
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={item.id}
              className="relative group"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Glass Portfolio Card */}
              <div className={`glass-card ${index % 2 === 0 ? 'glass-card-rose' : 'glass-card-lavender'} p-10 md:p-12 rounded-3xl transition-all duration-300 hover:scale-[1.02] overflow-hidden`}>
                {/* Glass highlights and reflections */}
                <div className="glass-card-highlight" />
                <div className="glass-card-shine" />
                <div className="glass-card-glow" />

                {/* Content */}
                <div className="relative z-10">
                  {/* Category Badge */}
                  <div className="inline-block mb-4">
                    <span className="glass-card px-4 py-2 rounded-full text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-lavender-400 overflow-hidden relative">
                      <div className="glass-card-highlight" />
                      <div className="glass-card-shine" />
                      <div className="glass-card-glow" />
                      <span className="relative z-10">{item.category} • {item.year}</span>
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl md:text-4xl font-black text-dark-600 mb-4 leading-tight">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-base md:text-lg text-dark-500 mb-6 leading-relaxed">
                    {item.description}
                  </p>

                  {/* Results */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-black text-dark-600 mb-3 uppercase tracking-wide">
                      {portfolioPage.resultsLabel}
                    </h4>
                    <ul className="space-y-2">
                      {item.results.map((result, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className={`mt-1.5 flex-shrink-0 ${index % 2 === 0 ? 'text-rose-500' : 'text-lavender-400'}`}>
                            ✓
                          </span>
                          <span className="text-base leading-relaxed text-dark-500">
                            {result}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <p className="text-xl text-dark-400 mb-8">
            {portfolioPage.ctaText}
          </p>
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            className="glass-card glass-card-rose px-10 py-4 rounded-full font-semibold text-base text-dark-600 overflow-hidden group hover:scale-105 transition-all duration-300 inline-flex items-center gap-2"
          >
            <div className="glass-card-highlight" />
            <div className="glass-card-shine group-hover:opacity-100" />
            <div className="glass-card-glow" />
            <span className="relative z-10 flex items-center gap-2">
              {portfolioPage.ctaButton}
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </button>
        </motion.div>
      </div>
    </div>
  );
};
